import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Listing, getShowSensitiveContent } from 'models/Listing';
import useShowEnquireAndWatchlist from 'hooks/useShowEnquireAndWatchlist';
import { Button } from 'ncoded-component-library';
import ChatIcon from 'icons/Chat.icon';
import useOpenEnquire from 'hooks/useOpenEnquire';
import AuthContext from 'providers/Auth/Auth.context';
import { Link } from 'react-router-dom';
import utils, {
  checkIsPublic,
  getBuyingPortfolio,
  getIsListingOwner,
  getPortfolioRoute,
  getResolveUrl,
  hasNotSeenDiscoveryFeature,
} from 'utils';
import StarIcon from 'icons/Star.icon';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import useWatchlist from 'hooks/useWatchlist';
import LinkToIcon from 'icons/LinkTo.icon';
import ShareListingModal from 'components/ShareListingModal';
import ShareIcon from 'icons/Share.icon';
import useCallbackRef from 'hooks/useCallbackRef';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import ShareListingWithPreviousLeads from 'components/ShareListingWithPreviousLeads';
import { FIFTH_RELEASE_DISCOVERING_FEATURE } from 'data/discoveringFeature';

import './ListingActions.styles.scss';

type ListingActionsProps = {
  className?: string;
  listing: Listing;
  showMessageBrokerButton?: boolean;
  onWatchlisted: (isWatchlisted: boolean) => void;
};

const ListingActions: React.FC<ListingActionsProps> = (props) => {
  const {
    className,
    listing,
    showMessageBrokerButton = false,
    onWatchlisted,
  } = props;

  const [shareListingModal, shareListingModalRef] = useCallbackRef<ModalRef>();
  const [shareWithPreviousLeadsModal, shareWithPreviousLeadsModalRef] =
    useCallbackRef<ModalRef>();

  const { isLoggedIn } = useContext(AuthContext);
  const { currentUser, isSuperAdmin } = useContext(CurrentUserContext);

  const {
    _id: listingId,
    listingTitle,
    listingEnquiryId,
    isEnquired,
    userId,
    isCoOwner,
    dealRoomId,
    stage,
    industry,
    slugName,
    type,
    isWatchlisted,
    sessionId,
  } = listing || {};

  const isListingOwner = getIsListingOwner(currentUser?._id, userId, isCoOwner);

  const isListingOwnerOrSuperAdmin = isSuperAdmin || isListingOwner;

  const redirectionListingLink = utils.composeBusinessUrl(
    industry,
    slugName,
    type,
  );

  const showSensitiveContent = useMemo(
    () => getShowSensitiveContent(listing),
    [listing],
  );

  const { t } = useTranslation();

  const { showEnquire, showWatchlist } = useShowEnquireAndWatchlist(listing);

  const openEnquire = useOpenEnquire();

  const { loading: watchlistRequestLoading, onWatchlist } = useWatchlist(
    listingId,
    isWatchlisted,
    onWatchlisted,
  );

  const EnquireButton = useMemo(
    () =>
      showEnquire && (
        <Button
          variant="outline"
          icon={ChatIcon}
          onClick={() => openEnquire(listing)}
        >
          {t('enquire')}
        </Button>
      ),
    [listing, openEnquire, showEnquire, t],
  );

  const PortfolioLink = useMemo(
    () =>
      (isListingOwnerOrSuperAdmin || isEnquired) && (
        <Link
          className="ncoded-button ncoded-button--link"
          to={getPortfolioRoute(
            !isListingOwnerOrSuperAdmin,
            dealRoomId,
            listingEnquiryId,
          )}
        >
          <LinkToIcon />
          {t('portfolio')}
        </Link>
      ),
    [dealRoomId, isEnquired, isListingOwnerOrSuperAdmin, listingEnquiryId, t],
  );

  const MessageBrokerLink = useMemo(
    () =>
      showMessageBrokerButton &&
      sessionId &&
      listingEnquiryId && (
        <Link
          className="ncoded-button ncoded-button--link"
          to={`${getBuyingPortfolio(
            listingEnquiryId,
            'messaging',
          )}/${sessionId}`}
        >
          <ChatIcon />
          {t('message')}
        </Link>
      ),
    [listingEnquiryId, sessionId, showMessageBrokerButton, t],
  );

  const WatchlistButton = useMemo(
    () =>
      isLoggedIn &&
      showWatchlist && (
        <Button
          className={classNames({
            fill: isWatchlisted,
          })}
          variant="link"
          icon={StarIcon}
          disabled={isListingOwner || watchlistRequestLoading}
          onClick={onWatchlist}
        >
          {t('shortlist')}
        </Button>
      ),
    [
      isListingOwner,
      isLoggedIn,
      isWatchlisted,
      onWatchlist,
      showWatchlist,
      t,
      watchlistRequestLoading,
    ],
  );

  const SocialShareButton = useMemo(
    () =>
      checkIsPublic(stage) ? (
        <>
          <Button
            className={classNames({
              'new-feature':
                isLoggedIn &&
                hasNotSeenDiscoveryFeature(
                  currentUser.seenShareListingFeature,
                  FIFTH_RELEASE_DISCOVERING_FEATURE,
                ),
            })}
            variant="link"
            icon={ShareIcon}
            onClick={shareListingModal?.open}
          >
            {t('share')}
          </Button>
          <ShareListingModal
            ref={shareListingModalRef}
            url={getResolveUrl(redirectionListingLink)}
            listingId={listingId}
            listingName={listingTitle}
            canShareWithPreviousLeads={isListingOwnerOrSuperAdmin}
            onChooseLeads={shareWithPreviousLeadsModal?.open}
          />
          <ShareListingWithPreviousLeads
            ref={shareWithPreviousLeadsModalRef}
            dealRoomId={dealRoomId}
            listingId={listingId}
            listingName={listingTitle}
          />
        </>
      ) : null,
    [
      stage,
      isLoggedIn,
      currentUser,
      dealRoomId,
      listingId,
      listingTitle,
      redirectionListingLink,
      isListingOwnerOrSuperAdmin,
      shareWithPreviousLeadsModal?.open,
      shareListingModal?.open,
      shareListingModalRef,
      shareWithPreviousLeadsModalRef,
      t,
    ],
  );

  const classes = classNames('listing-actions', className);

  return (
    <div className={classes}>
      {showSensitiveContent && EnquireButton}
      {PortfolioLink}
      {MessageBrokerLink}
      {showSensitiveContent && WatchlistButton}
      {SocialShareButton}
    </div>
  );
};

export default ListingActions;
